import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import { useGetTabletDevices } from '../hooks/reactQuerys/TabletQueries';
import { useCreateReadingSession } from '../hooks/reactQuerys/TabletReadingQueries';

const ONLINE_THRESHOLD_MINUTES = 2;

function isOnline(lastSeen?: string | null): boolean {
  if (!lastSeen) return false;
  const ts = new Date(lastSeen).getTime();
  if (Number.isNaN(ts)) return false;
  return Date.now() - ts <= ONLINE_THRESHOLD_MINUTES * 60_000;
}

interface DocLite {
  idDocument: number;
  FileName?: string | null;
  /** Optionaler Hinweis fuer den Selector (z.B. "Entwurf", "Urkunde"). */
  Kind?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  idLegalTransaction: number;
  documents: DocLite[];
}

export const StartReadingDialog: React.FC<Props> = ({
  open,
  onClose,
  idLegalTransaction,
  documents,
}) => {
  const { tabletDevices, isLoadingTabletDevices } = useGetTabletDevices();
  const { createReadingSession, isPendingCreateReadingSession } =
    useCreateReadingSession();

  const [idDocument, setIdDocument] = useState<number | ''>('');
  const [idTabletDevices, setIdTabletDevices] = useState<number[]>([]);
  const [controlMode, setControlMode] = useState<'push' | 'free'>('push');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setIdDocument(documents[0]?.idDocument ?? '');
      setIdTabletDevices([]);
      setControlMode('push');
      setError(null);
    }
  }, [open, documents]);

  const sortedDevices = useMemo(() => {
    const arr = [...(tabletDevices ?? [])];
    arr.sort((a, b) => {
      const oA = isOnline(a.LastSeen_at) ? 0 : 1;
      const oB = isOnline(b.LastSeen_at) ? 0 : 1;
      if (oA !== oB) return oA - oB;
      return (a.Name || '').localeCompare(b.Name || '');
    });
    return arr;
  }, [tabletDevices]);

  function toggleTablet(id: number) {
    setIdTabletDevices(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  async function handleStart() {
    setError(null);
    if (!idDocument) {
      setError('Bitte ein Dokument auswählen.');
      return;
    }
    if (idTabletDevices.length === 0) {
      setError('Bitte mindestens ein Tablet auswählen.');
      return;
    }
    try {
      await createReadingSession({
        idLegalTransaction,
        idDocument: Number(idDocument),
        idTabletDevices,
        ControlMode: controlMode,
      });
      onClose();
    } catch (e) {
      setError(
        'Mitlese-Sitzung konnte nicht gestartet werden. Möglicherweise ist eines der Tablets bereits in einer aktiven Sitzung.'
      );
    }
  }

  const noDocs = !documents || documents.length === 0;
  const noTablets =
    !isLoadingTabletDevices && (sortedDevices?.length ?? 0) === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Urkunde mitlesen starten</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {noDocs && (
            <Alert severity='warning'>
              Es ist noch kein Dokument am Vorgang hinterlegt. Bitte zuerst
              Entwurf oder Urkunde hochladen.
            </Alert>
          )}
          {noTablets && (
            <Alert severity='info'>Es ist noch kein Tablet gekoppelt.</Alert>
          )}

          <FormControl fullWidth disabled={noDocs}>
            <InputLabel id='reading-doc-label'>Dokument</InputLabel>
            <Select
              labelId='reading-doc-label'
              label='Dokument'
              value={idDocument}
              onChange={e => setIdDocument(Number(e.target.value))}
            >
              {documents.map(d => (
                <MenuItem key={d.idDocument} value={d.idDocument}>
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ width: '100%' }}
                  >
                    <Box sx={{ flex: 1 }}>
                      {d.FileName || `Dokument #${d.idDocument}`}
                    </Box>
                    {d.Kind && <Chip label={d.Kind} size='small' />}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>
              Tablets auswählen
            </Typography>
            <List
              dense
              sx={{
                maxHeight: 240,
                overflow: 'auto',
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {sortedDevices.map(d => (
                <ListItem key={d.idTabletDevice} disablePadding>
                  <ListItemButton
                    onClick={() => toggleTablet(d.idTabletDevice)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge='start'
                        checked={idTabletDevices.includes(d.idTabletDevice)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={d.Name}
                      secondary={isOnline(d.LastSeen_at) ? 'Online' : 'Offline'}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          <FormControl>
            <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
              Steuerung
            </Typography>
            <RadioGroup
              row
              value={controlMode}
              onChange={e => setControlMode(e.target.value as 'push' | 'free')}
            >
              <FormControlLabel
                value='push'
                control={<Radio />}
                label='Notar pusht Seite (alle Tablets folgen)'
              />
              <FormControlLabel
                value='free'
                control={<Radio />}
                label='Tablets blättern selbst'
              />
            </RadioGroup>
          </FormControl>

          {error && <Alert severity='error'>{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          onClick={handleStart}
          disabled={isPendingCreateReadingSession || noDocs || noTablets}
        >
          Starten
        </Button>
      </DialogActions>
    </Dialog>
  );
};
